/**
 * Vita shade picker — supports both Classical (A1…BL2) and 3D-Master
 * (0M1…5M3) systems with a tab toggle. Single onChange callback returns
 * the selected shade code (string) regardless of system.
 *
 * Used by both the public Send-a-Case workflow and the Admin → Cases
 * add-case modal so the two surfaces stay in lockstep.
 *
 * Props:
 *   value          — currently selected shade (string)
 *   onChange       — called with the next shade string
 *   variant        — "light" (default, on white card) or "dark" (on dark card)
 *   testIdPrefix   — prefix for data-testid (default "shade")
 *   defaultSystem  — initial tab when value is empty ("classical" | "3d-master").
 *                    The clinic's stored preference flows in here so the
 *                    form opens on their preferred system without a click.
 */
import { useEffect, useState } from "react";
import { SHADES, SHADES_3D_MASTER, detectShadeSystem } from "../lib/productCatalog";

export default function ShadePicker({ value, onChange, variant = "light", testIdPrefix = "shade", defaultSystem = "classical" }) {
    // Auto-pick the system that matches the existing value (if any), else
    // honor the prop-provided default. User can override either freely.
    const [system, setSystem] = useState(() => (value ? detectShadeSystem(value) : (defaultSystem || "classical")));

    // Two reasons to re-sync the system:
    // 1. Parent swaps in a shade string from a different system (e.g. editing
    //    a case that already has "2M2") — match the value.
    // 2. Parent's defaultSystem changed AND no value is selected — honour the
    //    new clinic preference (admin switches the client picker).
    useEffect(() => {
        if (value) {
            const sys = detectShadeSystem(value);
            if (sys !== system) setSystem(sys);
        } else if (defaultSystem && defaultSystem !== system) {
            setSystem(defaultSystem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, defaultSystem]);

    const options = system === "3d-master" ? SHADES_3D_MASTER : SHADES;

    const isDark = variant === "dark";
    const tabActiveCls = isDark
        ? "bg-[#0B1120] text-cyan-300 border border-cyan-400/40"
        : "bg-[#0B1120] text-cyan-300 border border-[#0B1120]";
    const tabIdleCls = isDark
        ? "bg-transparent text-slate-400 border border-slate-700/60 hover:border-slate-500"
        : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400";
    const chipActiveCls = isDark
        ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/60"
        : "bg-[#0B1120] text-cyan-300 border border-[#0B1120]";
    const chipIdleCls = isDark
        ? "bg-transparent text-slate-300 border border-slate-700/60 hover:border-slate-500"
        : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400";

    return (
        <div className="space-y-2.5">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100/70" data-testid={`${testIdPrefix}-system-toggle`}>
                <SystemTab
                    active={system === "classical"}
                    onClick={() => setSystem("classical")}
                    testId={`${testIdPrefix}-system-classical`}
                    activeCls={tabActiveCls}
                    idleCls={tabIdleCls}
                >
                    Classical
                </SystemTab>
                <SystemTab
                    active={system === "3d-master"}
                    onClick={() => setSystem("3d-master")}
                    testId={`${testIdPrefix}-system-3d-master`}
                    activeCls={tabActiveCls}
                    idleCls={tabIdleCls}
                >
                    3D-Master
                </SystemTab>
            </div>
            <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-options`}>
                {options.map((s) => (
                    <button
                        type="button"
                        key={s}
                        data-testid={`${testIdPrefix}-${s}`}
                        onClick={() => onChange(s)}
                        className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ${value === s ? chipActiveCls : chipIdleCls}`}
                    >
                        {s}
                    </button>
                ))}
            </div>
        </div>
    );
}

function SystemTab({ active, onClick, children, testId, activeCls, idleCls }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-3.5 py-1 rounded-full text-[11.5px] font-medium tracking-wide transition ${active ? activeCls : idleCls}`}
        >
            {children}
        </button>
    );
}
